import { useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnquiryDialog = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    contact_number: "",
    email: "",
    location: "",
    product_required: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/enquiry`, formData);
      toast.success("Enquiry submitted successfully! We'll contact you soon.");
      setFormData({
        name: "",
        company_name: "",
        contact_number: "",
        email: "",
        location: "",
        product_required: "",
        message: "",
      });
      onClose();
    } catch (error) {
      console.error("Enquiry submission error:", error);
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={onClose}
          data-testid="enquiry-dialog-overlay"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-sm shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            data-testid="enquiry-dialog"
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-2xl font-heading font-bold text-primary-main" data-testid="enquiry-dialog-title">
                SEND ENQUIRY
              </h2>
              <button
                onClick={onClose}
                className="text-secondary-main hover:text-primary-main transition-colors"
                data-testid="enquiry-dialog-close"
                aria-label="Close dialog"
              >
                <X size={24} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                  data-testid="enquiry-input-name"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  required
                  data-testid="enquiry-input-company"
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                  Contact Number <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  value={formData.contact_number}
                  onChange={(e) => handleChange("contact_number", e.target.value)}
                  required
                  data-testid="enquiry-input-phone"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                  Email ID <span className="text-red-500">*</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  required
                  data-testid="enquiry-input-email"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                  Location <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                  required
                  data-testid="enquiry-input-location"
                  placeholder="City, State"
                />
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                  Product Required <span className="text-red-500">*</span>
                </label>
                <Select
                  value={formData.product_required}
                  onValueChange={(value) => handleChange("product_required", value)}
                  required
                >
                  <SelectTrigger data-testid="enquiry-select-product">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single Phase Servo Stabilizer" data-testid="product-option-single-phase">
                      Single Phase Servo Stabilizer
                    </SelectItem>
                    <SelectItem value="Three Phase Servo Stabilizer" data-testid="product-option-three-phase">
                      Three Phase Servo Stabilizer
                    </SelectItem>
                    <SelectItem value="Three Phase Oil Cooled Servo Stabilizer" data-testid="product-option-oil-cooled">
                      Three Phase Oil Cooled Servo Stabilizer
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  required
                  rows={4}
                  data-testid="enquiry-input-message"
                  placeholder="Tell us about your requirements"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-primary-main text-white hover:bg-primary-light rounded-sm"
                  data-testid="enquiry-submit-button"
                >
                  {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                </Button>
                <Button
                  type="button"
                  onClick={onClose}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="enquiry-cancel-button"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EnquiryDialog;
